<template>
  <form class="form-horizontal">
    <div
      v-bind:class="{ 'has-error': $v.line[F_BUS].$invalid }"
      class="form-group"
    >
      <div class="col-4 col-sm-12">
        <label class="form-label" for="f-bus">From Bus</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <input
            v-model.number="line[F_BUS]"
            class="form-input"
            id="f-bus"
            inputmode="numeric"
          />
          <button
            v-on:click="navigateToBus(line[F_BUS])"
            type="button"
            class="btn input-group-addon"
            aria-label="Navigate to 'from' bus"
          >
            <i v-if="busExists(line[F_BUS])" class="icon icon-arrow-right"></i>
          </button>
        </div>
        <p v-if="!$v.line[F_BUS].required" class="form-input-hint">
          From bus number is required.
        </p>
        <p v-else-if="!$v.line[F_BUS].integer" class="form-input-hint">
          From bus number must be an integer.
        </p>
        <p v-else-if="!$v.line[F_BUS].minValue" class="form-input-hint">
          From bus number must be >= 1.
        </p>
        <p v-else-if="!$v.line[F_BUS].exists" class="form-input-hint">
          From bus {{ line[F_BUS] }} must exist.
        </p>
      </div>
    </div>

    <div
      v-bind:class="{ 'has-error': $v.line[T_BUS].$invalid }"
      class="form-group"
    >
      <div class="col-4 col-sm-12">
        <label class="form-label" for="t-bus">To Bus</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <input
            v-model.number="line[T_BUS]"
            class="form-input"
            id="t-bus"
            inputmode="numeric"
          />
          <button
            v-on:click="navigateToBus(line[T_BUS])"
            type="button"
            class="btn input-group-addon"
            aria-label="Navigate to 'to' bus"
          >
            <i v-if="busExists(line[T_BUS])" class="icon icon-arrow-right"></i>
          </button>
        </div>
        <p v-if="!$v.line[T_BUS].required" class="form-input-hint">
          To bus number is required.
        </p>
        <p v-else-if="!$v.line[T_BUS].integer" class="form-input-hint">
          To bus number must be an integer.
        </p>
        <p v-else-if="!$v.line[T_BUS].minValue" class="form-input-hint">
          To bus number must be >= 1.
        </p>
        <p v-else-if="!$v.line[T_BUS].exists" class="form-input-hint">
          To bus {{ line[T_BUS] }} must exist.
        </p>
      </div>
    </div>

    <div
      v-bind:class="{ 'has-error': $v.line[BR_STATUS].$invalid }"
      class="form-group"
    >
      <div class="col-4 col-sm-12">
        <label class="form-label" for="status">Status</label>
      </div>
      <div class="col-8 col-sm-12">
        <div class="input-group">
          <label class="form-checkbox">
            <input
              v-model.number="line[BR_STATUS]"
              v-bind:true-value="1"
              v-bind:false-value="0"
              type="checkbox"
              id="status"
            />
            <i class="form-icon"></i> In Service
          </label>
        </div>
        <p v-if="!$v.line[BR_STATUS].between" class="form-input-hint">
          Status must be 0 or 1.
        </p>
      </div>
    </div>

    <fieldset>
      <h6>Loss Coefficients</h6>

      <div
        v-bind:class="{ 'has-error': $v.line[LOSS0].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="loss0">Constant</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">l<sub>0</sub></span>
            <input
              v-model.number="line[LOSS0]"
              class="form-input"
              id="loss0"
              inputmode="decimal"
            />
          </div>
          <p v-if="!$v.line[LOSS0].required" class="form-input-hint">
            Coefficient of constant term is required.
          </p>
          <p v-else-if="!$v.line[LOSS0].decimal" class="form-input-hint">
            Coefficient of constant term must be a decimal.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.line[LOSS1].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="loss1">Linear</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">l<sub>1</sub></span>
            <input
              v-model.number="line[LOSS1]"
              class="form-input"
              id="loss1"
              inputmode="decimal"
            />
          </div>
          <p v-if="!$v.line[LOSS1].required" class="form-input-hint">
            Coefficient of linear term is required.
          </p>
          <p v-else-if="!$v.line[LOSS1].decimal" class="form-input-hint">
            Coefficient of linear term must be a decimal.
          </p>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <h6>Voltage Magnitude</h6>

      <div
        v-bind:class="{ 'has-error': $v.line[VF].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="vf">From Bus</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">V<sub>f</sub></span>
            <input
              v-model.number="line[VF]"
              class="form-input"
              id="vf"
              inputmode="decimal"
            />
            <span class="input-group-addon">pu</span>
          </div>
          <p v-if="!$v.line[VF].required" class="form-input-hint">
            Voltage at "from" bus is required.
          </p>
          <p v-else-if="!$v.line[VF].decimal" class="form-input-hint">
            Voltage at "from" bus must be a decimal.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.line[VT].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="vt">To Bus</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">V<sub>t</sub></span>
            <input
              v-model.number="line[VT]"
              class="form-input"
              id="vt"
              inputmode="decimal"
            />
            <span class="input-group-addon">pu</span>
          </div>
          <p v-if="!$v.line[VT].required" class="form-input-hint">
            Voltage at "to" bus is required.
          </p>
          <p v-else-if="!$v.line[VT].decimal" class="form-input-hint">
            Voltage at "to" bus must be a decimal.
          </p>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <h6>Active Power</h6>

      <div
        v-bind:class="{ 'has-error': $v.line[PMIN].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="pmin">
            Lower Limit <span v-if="line[PMIN] > 0">From</span>
            <span v-else-if="line[PMIN] < 0">To</span>
          </label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">
              P<sub v-if="line[PMIN] > 0">f</sub>
              <sub v-else-if="line[PMIN] < 0">t</sub>
              <sup>min</sup>
            </span>
            <input
              v-model.number="line[PMIN]"
              class="form-input"
              id="pmin"
              inputmode="decimal"
            />
            <span class="input-group-addon">MW</span>
          </div>
          <p v-if="!$v.line[PMIN].required" class="form-input-hint">
            Minimum active power is required.
          </p>
          <p v-else-if="!$v.line[PMIN].decimal" class="form-input-hint">
            Minimum active power must be a decimal.
          </p>
          <p v-else-if="line[PMIN] > 0" class="form-input-hint mb-0">
            Negative to limit P<sub>t</sub>.
          </p>
          <p v-else-if="line[PMIN] < 0" class="form-input-hint mb-0">
            Positive to limit P<sub>f</sub>.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.line[PMAX].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="pmax">
            Upper Limit <span v-if="line[PMAX] > 0">From</span>
            <span v-else-if="line[PMAX] < 0">To</span>
          </label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">
              P<sub v-if="line[PMAX] > 0">f</sub>
              <sub v-else-if="line[PMAX] < 0">t</sub>
              <sup>max</sup>
            </span>
            <input
              v-model.number="line[PMAX]"
              class="form-input"
              id="pmax"
              inputmode="decimal"
            />
            <span class="input-group-addon">MW</span>
          </div>
          <p v-if="!$v.line[PMAX].required" class="form-input-hint">
            Maximum active power is required.
          </p>
          <p v-else-if="!$v.line[PMAX].decimal" class="form-input-hint">
            Maximum active power must be a decimal.
          </p>
          <p v-else-if="line[PMAX] > 0" class="form-input-hint mb-0">
            Negative to limit P<sub>t</sub>.
          </p>
          <p v-else-if="line[PMAX] < 0" class="form-input-hint mb-0">
            Positive to limit P<sub>f</sub>.
          </p>
        </div>
      </div>
    </fieldset>

    <fieldset>
      <h6>Reactive Power</h6>

      <div
        v-bind:class="{ 'has-error': $v.line[QMINF].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="qminf">Lower Limit From</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">Q<sub>f</sub><sup>min</sup></span>
            <input
              v-model.number="line[QMINF]"
              class="form-input"
              id="qminf"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.line[QMINF].required" class="form-input-hint">
            Minimum reactive power is required.
          </p>
          <p v-else-if="!$v.line[QMINF].decimal" class="form-input-hint">
            Minimum reactive power must be a decimal.
          </p>
        </div>
      </div>
      <div
        v-bind:class="{ 'has-error': $v.line[QMAXF].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="qmaxf">Upper Limit From</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">Q<sub>f</sub><sup>max</sup></span>
            <input
              v-model.number="line[QMAXF]"
              class="form-input"
              id="qmaxf"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.line[QMAXF].required" class="form-input-hint">
            Maximum reactive power is required.
          </p>
          <p v-else-if="!$v.line[QMAXF].decimal" class="form-input-hint">
            Maximum reactive power must be a decimal.
          </p>
        </div>
      </div>

      <div
        v-bind:class="{ 'has-error': $v.line[QMINT].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="qmint">Lower Limit To</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">Q<sub>t</sub><sup>min</sup></span>
            <input
              v-model.number="line[QMINT]"
              class="form-input"
              id="qmint"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.line[QMINT].required" class="form-input-hint">
            Minimum reactive power is required.
          </p>
          <p v-else-if="!$v.line[QMINT].decimal" class="form-input-hint">
            Minimum reactive power must be a decimal.
          </p>
        </div>
      </div>
      <div
        v-bind:class="{ 'has-error': $v.line[QMAXT].$invalid }"
        class="form-group"
      >
        <div class="col-4 col-sm-12">
          <label class="form-label" for="qmaxt">Upper Limit To</label>
        </div>
        <div class="col-8 col-sm-12">
          <div class="input-group">
            <span class="input-group-addon">Q<sub>t</sub><sup>max</sup></span>
            <input
              v-model.number="line[QMAXT]"
              class="form-input"
              id="qmaxt"
              inputmode="decimal"
            />
            <span class="input-group-addon">MVAr</span>
          </div>
          <p v-if="!$v.line[QMAXT].required" class="form-input-hint">
            Maximum reactive power is required.
          </p>
          <p v-else-if="!$v.line[QMAXT].decimal" class="form-input-hint">
            Maximum reactive power must be a decimal.
          </p>
        </div>
      </div>
    </fieldset>
  </form>
</template>

<script>
import {
  required,
  minValue,
  integer,
  decimal,
  between
} from "vuelidate/lib/validators";

import MPCStore from "@/stores/MPCStore";

import {
  DCLINE_F_BUS,
  DCLINE_T_BUS,
  DCLINE_BR_STATUS,
  DCLINE_PF,
  DCLINE_PT,
  DCLINE_QF,
  DCLINE_QT,
  DCLINE_VF,
  DCLINE_VT,
  DCLINE_PMIN,
  DCLINE_PMAX,
  DCLINE_QMINF,
  DCLINE_QMAXF,
  DCLINE_QMINT,
  DCLINE_QMAXT,
  DCLINE_LOSS0,
  DCLINE_LOSS1
} from "../../stores/idx";

export default {
  name: "DCLineForm",
  props: ["ord"],
  data() {
    return {
      line: MPCStore.methods.getDCLine(+this.ord - 1),
      F_BUS: DCLINE_F_BUS,
      T_BUS: DCLINE_T_BUS,
      VF: DCLINE_VF,
      VT: DCLINE_VT,
      PMIN: DCLINE_PMIN,
      PMAX: DCLINE_PMAX,
      QMINF: DCLINE_QMINF,
      QMAXF: DCLINE_QMAXF,
      QMINT: DCLINE_QMINT,
      QMAXT: DCLINE_QMAXT,
      LOSS0: DCLINE_LOSS0,
      LOSS1: DCLINE_LOSS1,
      BR_STATUS: DCLINE_BR_STATUS,
      PF: DCLINE_PF,
      PT: DCLINE_PT,
      QF: DCLINE_QF,
      QT: DCLINE_QT
    };
  },
  validations() {
    return {
      line: {
        [DCLINE_F_BUS]: {
          required,
          integer,
          minValue: minValue(1),
          exists: this.busExists
        },
        [DCLINE_T_BUS]: {
          required,
          integer,
          minValue: minValue(1),
          exists: this.busExists
        },
        [DCLINE_LOSS0]: { required, decimal },
        [DCLINE_LOSS1]: { required, decimal },
        [DCLINE_VF]: { required, decimal },
        [DCLINE_VT]: { required, decimal },
        [DCLINE_PMIN]: { required, decimal },
        [DCLINE_PMAX]: { required, decimal },
        [DCLINE_QMINF]: { required, decimal },
        [DCLINE_QMAXF]: { required, decimal },
        [DCLINE_QMINT]: { required, decimal },
        [DCLINE_QMAXT]: { required, decimal },
        [DCLINE_BR_STATUS]: { between: between(0, 1) }
      }
    };
  },
  methods: {
    navigateToBus(n) {
      let idx = MPCStore.methods.findBusIdx(n);
      this.$router.push({ name: "bus", params: { ord: idx + 1 } });
    },
    busExists: n => MPCStore.methods.findBusIdx(n) >= 0
  }
};
</script>

<style lang="scss" scoped>
@import "../../main.scss";

fieldset {
  margin-top: 1rem;
  border-top: 0.05rem solid $gray-color;
  padding-top: 0.5rem;
}
</style>
